import styled from "@emotion/styled";
import type { StyledButtonProps } from "./SvyButton.interface";
import { css } from "@emotion/react";

const DEFAULT_COLOR = "primary" as const;

export const StyledButton = styled.button<StyledButtonProps>`
    --svy-button-background-color: ${({ color = DEFAULT_COLOR, theme }) =>
        theme.colors[color as keyof typeof theme.colors][500]};
    --svy-button-hover-background-color: ${({ color = DEFAULT_COLOR, theme }) =>
        theme.colors[color as keyof typeof theme.colors][600]};
    --svy-button-active-background-color: ${({ color = DEFAULT_COLOR, theme }) =>
        theme.colors[color as keyof typeof theme.colors][400]};

    --svy-button-text-color: ${({ theme }) => theme.colors.neutral[100]};
    --svy-button-text-size: 0;

    --svy-button-horizontal-padding: 0px;
    --svy-button-vertical-padding: 0px;
    --svy-button-border-size: 0px;
    --svy-button-border-color: transparent;
    --svy-button-side-elements-margin: 0;

    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: row;
    outline: none;
    overflow: hidden;
    position: relative;
    border-radius: 8px;
    font-weight: 700;

    transition: all 0.2s ease-in-out;

    border: var(--svy-button-border-size) solid var(--svy-button-border-color);

    background-color: var(--svy-button-background-color);

    color: var(--svy-button-text-color);
    font-size: var(--svy-button-text-size);

    height: var(--svy-button-height);
    max-height: var(--svy-button-height);

    padding: 0 calc(var(--svy-button-horizontal-padding) - var(--svy-button-border-size) * 2);

    &:not(:disabled) {
        cursor: pointer;
    }

    &:not(:disabled):hover {
        background-color: var(--svy-button-hover-background-color);
    }
    &:not(:disabled):active {
        background-color: var(--svy-button-active-background-color);
    }

    &:disabled {
        --svy-button-background-color: ${({ theme }) => theme.colors.neutral[900]};
        opacity: 0.05;
    }

    ${({ variant, theme, color = DEFAULT_COLOR }) => {
        switch (variant) {
            case "outline":
                return css`
                    --svy-button-border-size: 1px;
                    --svy-button-border-color: ${theme.colors[color as keyof typeof theme.colors][500]};
                    --svy-button-text-color: ${theme.colors[color as keyof typeof theme.colors][500]};
                    --svy-button-background-color: transparent;
                    --svy-button-hover-background-color: ${theme.colors[color as keyof typeof theme.colors][100]};
                    --svy-button-active-background-color: ${theme.colors[color as keyof typeof theme.colors][50]};
                `;
            case "text":
                return css`
                    --svy-button-background-color: transparent;
                    --svy-button-text-color: ${theme.colors[color as keyof typeof theme.colors][500]};
                    --svy-button-hover-background-color: ${theme.colors[color as keyof typeof theme.colors][50]};
                    --svy-button-active-background-color: ${theme.colors[color as keyof typeof theme.colors][100]};
                `;
            case "default":
            default:
                return css``;
        }
    }}

    ${({ size }) => {
        switch (size) {
            case "s":
                return css`
                    --svy-button-text-size: 14px;
                    --svy-button-horizontal-padding: 16px;
                    --svy-button-side-elements-margin: 4px;
                    --svy-button-height: 40px;
                `;
            case "l":
                return css`
                    --svy-button-text-size: 20px;
                    --svy-button-horizontal-padding: 32px;
                    --svy-button-side-elements-margin: 8px;
                    --svy-button-height: 56px;
                `;
            case "m":
            default:
                return css`
                    --svy-button-text-size: 16px;
                    --svy-button-horizontal-padding: 24px;
                    --svy-button-side-elements-margin: 8px;
                    --svy-button-height: 48px;
                `;
        }
    }}
`;

export const ElementRightContainer = styled.span`
    margin-left: var(--svy-button-side-elements-margin);
`;

export const ElementLeftContainer = styled.span`
    margin-right: var(--svy-button-side-elements-margin);
`;

export const LoadingContainer = styled.span`
    &,
    &:before {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
    }

    &:before {
        content: "";
        background: ${({ theme }) => theme.colors.neutral[500]};
        opacity: 0.8;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    > span {
        font-size: calc(var(--svy-button-text-size) * 1.4);
        animation-name: spin;
        animation-duration: 1000ms;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
    }
`;
